import { useState } from "react";
import { signInWithPopup } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "bootstrap-icons/font/bootstrap-icons.css";

import { auth, googleProvider } from "../../utils/firebase";
import { loginWithGoogleUser } from "../../apis/loginWithGoogleUser";
import { login } from "../../apis/login";
import Input from "../campaign/wizard/Input";
import {
    AccountLockedError,
    ActivationRequiredError,
    getApiErrorDetail,
} from "../../utils/authErrors";
import { setAuthSession, setGoogleAuthSession } from "../../utils/authSession";
import resolvePostAuthPath from "../../utils/resolvePostAuthPath";
import { APP_ROUTES } from "../../constants/appRoutes";
import { useAuthModal } from "../../context/AuthModalContext";
import { trackCtaClicked } from "../../analytics/analytics";
import { ANALYTICS_CTAS } from "../../analytics/analytics.constants";

const GOOGLE_ACCOUNT_MESSAGE =
    "This account uses Google sign-in. Please log in with Google.";

export default function LoginForm({
    redirectPath = null,
    onAuthenticated = null,
    onAuthSuccess = null,
    onSwitchToRegister = null,
    onActivationRequired = null,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openAuthModal } = useAuthModal();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showGoogleCta, setShowGoogleCta] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);

    const completeAuth = async () => {
        if (onAuthSuccess) {
            await onAuthSuccess();
            return;
        }

        if (onAuthenticated) {
            onAuthenticated();
            return;
        }

        const path = await resolvePostAuthPath(redirectPath);
        navigate(path);
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setShowGoogleCta(false);
        setLoggingIn(true);

        try {
            const result = await login({ email, password });
            setAuthSession(dispatch, result.user, result.access_token);
            await completeAuth();
        } catch (err) {
            if (err instanceof ActivationRequiredError) {
                toast.info("Your account is not activated yet. Enter the code from your email.");
                if (onActivationRequired) {
                    onActivationRequired(email);
                } else {
                    navigate(`${APP_ROUTES.ACTIVATE}?email=${encodeURIComponent(email)}`);
                }
                return;
            }
            if (err instanceof AccountLockedError) {
                navigate(APP_ROUTES.ACCOUNT_LOCKED);
                return;
            }

            const detail = getApiErrorDetail(err);
            if (detail.includes("Google sign-in")) {
                setShowGoogleCta(true);
                return;
            }

            toast.error(detail === "Invalid credentials" ? "Invalid email or password." : detail);
        } finally {
            setLoggingIn(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            const res = await signInWithPopup(auth, googleProvider);
            const loginDetails = await loginWithGoogleUser({
                uid: res.user.uid,
                email: res.user.email,
                email_verified: res.user.emailVerified,
                display_name: res.user.displayName,
                is_anonymous: res.user.isAnonymous,
                photo_url: res.user.photoURL,
            });
            setGoogleAuthSession(dispatch, res.user, loginDetails.access_token);
            await completeAuth();
        } catch (err) {
            if (err.code === "auth/popup-closed-by-user") return;
            toast.error(getApiErrorDetail(err) || "Unable to sign in with Google. Please try again.");
            console.error({ err });
        }
    };

    return (
        <>
            <form onSubmit={handleEmailLogin}>
                <Input
                    label="Email address"
                    labelIcon="envelope"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    wrapperClassName="mb-4"
                />
                <Input
                    label="Password"
                    labelIcon="lock"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    wrapperClassName="mb-5"
                />

                {showGoogleCta ? (
                    <p className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                        {GOOGLE_ACCOUNT_MESSAGE}
                    </p>
                ) : null}

                <div className="grid gap-3">
                    <button
                        type="submit"
                        className="btn btn-primary-app w-full font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={loggingIn}
                    >
                        {loggingIn ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden
                                />
                                Logging in…
                            </>
                        ) : (
                            "Login"
                        )}
                    </button>
                    <Link
                        className="text-sm ps-1 font-semibold text-slate-700 hover:text-slate-900"
                        to={APP_ROUTES.FORGOT_PASSWORD}
                    >
                        Forgot password?
                    </Link>
                    <div className="text-center text-sm text-slate-500">Or</div>
                    <button
                        type="button"
                        className="btn btn-secondary-app w-full font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={handleGoogleLogin}
                        disabled={loggingIn}
                    >
                        Sign in with Google <i className="bi bi-google ms-1" aria-hidden />
                    </button>
                </div>
            </form>
            <p className="mt-5 text-center text-sm text-slate-600">
                Do not have an account?{" "}
                {onSwitchToRegister ? (
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 font-semibold text-slate-900 hover:text-slate-700"
                        onClick={() => {
                            trackCtaClicked(ANALYTICS_CTAS.AUTH_REGISTER);
                            onSwitchToRegister?.();
                        }}
                    >
                        Register
                    </button>
                ) : (
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 font-semibold text-slate-900 hover:text-slate-700"
                        onClick={() => {
                            trackCtaClicked(ANALYTICS_CTAS.AUTH_REGISTER);
                            openAuthModal({ initialTab: "register" });
                        }}
                    >
                        Register
                    </button>
                )}
            </p>
        </>
    );
}
